/**
 * Service de connexion Socket.io
 */

import { io, Socket } from 'socket.io-client';
import type { GameState, Position, Wall } from '@pathblock/core';
import { useGameStore } from '../store/gameStore';
import { soundService } from './soundService';

const SESSION_KEY = 'pathblock_session';

interface StoredSession {
  token: string;
  roomId: string;
  playerId: 1 | 2;
  playerName: string;
}

interface ServerToClientEvents {
  roomCreated: (data: { roomId: string; playerId: 1 | 2; token: string }) => void;
  roomJoined: (data: { roomId: string; playerId: 1 | 2; token: string }) => void;
  gameStart: (data: { gameState: GameState }) => void;
  gameUpdate: (data: { gameState: GameState }) => void;
  gameEnd: (data: { winner: 1 | 2; reason: string }) => void;
  error: (data: { message: string }) => void;
  playerDisconnected: (data: { playerId: 1 | 2 }) => void;
  timerUpdate: (data: { remainingTime: number }) => void;
  waitingForReconnect: (data: { playerId: 1 | 2 }) => void;
  rejoinResult: (data: { success: boolean; gameState?: GameState; roomId?: string; playerId?: 1 | 2; error?: string }) => void;
  matchFound: (data: { roomId: string; playerId: 1 | 2; token: string }) => void;
}

interface ClientToServerEvents {
  createRoom: (data: { playerName: string; turnTimeLimit: number | null }) => void;
  createRoomVsAI: (data: { playerName: string; turnTimeLimit: number | null; aiDifficulty?: string }) => void;
  joinRoom: (data: { roomId: string; playerName: string }) => void;
  choosePosition: (data: { roomId: string; col: number }) => void;
  move: (data: { roomId: string; to: Position }) => void;
  placeWall: (data: { roomId: string; wall: Wall }) => void;
  leaveRoom: (data: { roomId: string }) => void;
  rejoinRoom: (data: { token: string }) => void;
  joinMatchmaking: (data: { playerName: string }) => void;
  leaveMatchmaking: () => void;
}

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

class SocketService {
  private socket: GameSocket | null = null;

  connect(): void {
    if (this.socket?.connected) return;

    this.socket = io({
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.setupListeners();
  }

  private saveSession(token: string, roomId: string, playerId: 1 | 2, playerName: string): void {
    const session: StoredSession = { token, roomId, playerId, playerName };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  private clearSession(): void {
    localStorage.removeItem(SESSION_KEY);
  }

  private getSession(): StoredSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private setupListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connecté au serveur');
      useGameStore.setState({ connected: true, error: null });

      // Toujours tenter de rejoindre si une session existe (reload de page ou reconnexion réseau)
      const session = this.getSession();
      if (session) {
        this.socket?.emit('rejoinRoom', { token: session.token });
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Déconnecté du serveur');
      useGameStore.setState({ connected: false });
    });

    this.socket.on('roomCreated', ({ roomId, playerId, token }) => {
      console.log(`Room créée: ${roomId}, joueur: ${playerId}`);
      const playerName = useGameStore.getState().playerName;
      this.saveSession(token, roomId, playerId, playerName);
      useGameStore.setState({
        roomId,
        playerId,
        view: 'waiting',
        error: null,
      });
    });

    this.socket.on('roomJoined', ({ roomId, playerId, token }) => {
      console.log(`Room rejointe: ${roomId}, joueur: ${playerId}`);
      const playerName = useGameStore.getState().playerName;
      this.saveSession(token, roomId, playerId, playerName);
      useGameStore.setState({
        roomId,
        playerId,
        view: 'waiting',
        error: null,
      });
    });

    this.socket.on('matchFound', ({ roomId, playerId, token }) => {
      console.log(`Match trouvé: room ${roomId}, joueur: ${playerId}`);
      const playerName = useGameStore.getState().playerName;
      this.saveSession(token, roomId, playerId, playerName);
      useGameStore.setState({
        roomId,
        playerId,
        matchmakingActive: false,
        view: 'waiting',
        error: null,
      });
    });

    this.socket.on('gameStart', ({ gameState }) => {
      console.log('Partie démarrée');
      useGameStore.setState({
        gameState,
        view: 'playing',
        error: null,
        gameStartTime: Date.now(),
      });
    });

    this.socket.on('gameUpdate', ({ gameState }) => {
      const prevState = useGameStore.getState().gameState;
      const myPlayerId = useGameStore.getState().playerId;

      useGameStore.setState({
        gameState,
        wallPreview: null,
        placingWall: false,
        opponentDisconnected: false,
      });

      if (gameState.phase === 'finished') {
        useGameStore.setState({ view: 'finished' });
      }

      // Sons basés sur la dernière action
      if (gameState.lastAction && prevState) {
        const action = gameState.lastAction;

        // Ajouter à l'historique
        useGameStore.getState().addMoveHistory(action);

        if (action.type === 'move') {
          soundService.move();
        } else if (action.type === 'wall') {
          soundService.wall();
        }
        // Notifier si c'est maintenant notre tour
        if (gameState.phase === 'playing' && gameState.currentPlayer === myPlayerId &&
            prevState.currentPlayer !== myPlayerId) {
          soundService.turnStart();
        }
      }
    });

    this.socket.on('gameEnd', ({ winner, reason }) => {
      const playerId = useGameStore.getState().playerId;
      const isWinner = winner === playerId;
      const message = isWinner ? `Victoire ! ${reason}` : `Défaite... ${reason}`;

      if (isWinner) {
        soundService.win();
      } else {
        soundService.lose();
      }

      this.clearSession();
      useGameStore.setState({
        view: 'finished',
        endMessage: message,
      });
    });

    this.socket.on('error', ({ message }) => {
      console.error('Erreur serveur:', message);
      useGameStore.setState({ error: message });

      // Effacer l'erreur après 3 secondes
      setTimeout(() => {
        useGameStore.setState({ error: null });
      }, 3000);
    });

    this.socket.on('playerDisconnected', ({ playerId }) => {
      console.log(`Joueur ${playerId} déconnecté`);
    });

    this.socket.on('timerUpdate', ({ remainingTime }) => {
      useGameStore.setState({ remainingTime });
      // Son d'alerte si c'est notre tour et temps faible
      const state = useGameStore.getState();
      if (remainingTime <= 10 && remainingTime > 0 && state.gameState?.currentPlayer === state.playerId) {
        soundService.timerWarning();
      }
    });

    this.socket.on('waitingForReconnect', ({ playerId }) => {
      console.log(`Joueur ${playerId} déconnecté — attente reconnexion`);
      useGameStore.setState({ opponentDisconnected: true });
    });

    this.socket.on('rejoinResult', ({ success, gameState, roomId, playerId, error }) => {
      if (success && gameState && roomId && playerId) {
        console.log(`Reconnexion réussie dans room ${roomId}`);
        const session = this.getSession();
        useGameStore.setState({
          roomId,
          playerId,
          gameState,
          view: 'playing',
          error: null,
          playerName: session?.playerName ?? useGameStore.getState().playerName,
        });
      } else {
        // Session expirée ou introuvable : effacer silencieusement, rester au lobby
        console.log('Reconnexion échouée (session expirée):', error);
        this.clearSession();
      }
    });
  }

  createRoom(playerName: string, turnTimeLimit: number | null): void {
    this.socket?.emit('createRoom', { playerName, turnTimeLimit });
  }

  createRoomVsAI(playerName: string, turnTimeLimit: number | null, aiDifficulty: 'easy' | 'medium' | 'hard' = 'medium'): void {
    useGameStore.setState({ isVsAI: true });
    this.socket?.emit('createRoomVsAI', { playerName, turnTimeLimit, aiDifficulty });
  }

  joinRoom(roomId: string, playerName: string): void {
    this.socket?.emit('joinRoom', { roomId, playerName });
  }

  joinMatchmaking(playerName: string): void {
    this.socket?.emit('joinMatchmaking', { playerName });
  }

  leaveMatchmaking(): void {
    this.socket?.emit('leaveMatchmaking');
  }

  choosePosition(col: number): void {
    const roomId = useGameStore.getState().roomId;
    if (roomId) {
      this.socket?.emit('choosePosition', { roomId, col });
    }
  }

  move(to: Position): void {
    const roomId = useGameStore.getState().roomId;
    if (roomId) {
      this.socket?.emit('move', { roomId, to });
    }
  }

  placeWall(wall: Wall): void {
    const roomId = useGameStore.getState().roomId;
    if (roomId) {
      this.socket?.emit('placeWall', { roomId, wall });
    }
  }

  leaveRoom(): void {
    const roomId = useGameStore.getState().roomId;
    if (roomId) {
      this.socket?.emit('leaveRoom', { roomId });
    }
    this.clearSession();
    const name = useGameStore.getState().playerName;
    useGameStore.getState().reset();
    useGameStore.setState({ playerName: name, isVsAI: false, gameStartTime: null });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }
}

export const socketService = new SocketService();
